import { useMutation } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { useSnackbar } from 'ui-components';
import { PDF_DOCUMENT_TYPE, SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';
import { logger } from '~/core/logger/logger';
import { pdfApi } from '~/pdfApi';
import { ICreator } from '~/types';

interface PdfData {
  creators: Partial<ICreator>[];
  agency: {
    name?: string;
    logo?: string;
  };
  listName?: string;
}

export const useDownloadPdf = () => {
  const snackbar = useSnackbar();

  return useMutation({
    mutationFn: async ({
      pdfData,
      fileName,
    }: {
      pdfData: PdfData;
      fileName: string;
    }) => {
      if (!pdfData.creators || pdfData.creators.length === 0) {
        logger
          .withMetadata({
            listName: pdfData.listName,
            section: 'pdf-generation',
          })
          .warn('No content available to generate a PDF from.');
        throw new Error("There's no content available to generate a PDF from.");
      }

      // API Call to Generate PDF
      const pdfStream = await pdfApi.create({
        pdfType: PDF_DOCUMENT_TYPE.SAVED_LIST_CONTENT,
        data: pdfData,
      });

      // Validate and Save PDF
      if (pdfStream && pdfStream.data) {
        const data = pdfStream.data;
        saveAs(data, fileName); // Use file-saver to initiate download
        return { success: true };
      } else {
        // Handle cases where API returns 200 OK but data is unexpectedly empty/missing
        throw new Error(
          'PDF API returned an unexpected response structure or empty data.',
        );
      }
    },
    onSuccess: (_, { pdfData }) => {
      snackbar.success({
        description: 'PDF downloaded successfully!',
      });
      logger
        .withMetadata({
          listName: pdfData.listName,
          section: 'pdf-generation',
        })
        .info('Saved List PDF downloaded successfully');
    },
    onError: (error: Error, { pdfData }) => {
      if (
        error.message === "There's no content available to generate a PDF from."
      ) {
        snackbar.warning({
          description: error.message,
        });
      } else {
        snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
        logger
          .withMetadata({
            listName: pdfData.listName,
            section: 'pdf-generation',
            error,
          })
          .error('Error exporting the Saved List PDF');
      }
    },
  });
};
